/*
  A minimal prometheus metric registry.

  Only what omegga exports is implemented: counters, gauges, and histograms.
  Everything a registry holds can be turned into a plain `MetricFamily[]`
  snapshot, which is what makes the same class usable inside a plugin worker -
  the worker keeps its own registry and ships snapshots to the host, which
  renders them alongside its own (see ./plugin.ts).
*/

/** valid metric name (`:` is reserved for recording rules, but is legal) */
const NAME_RE = /^[a-zA-Z_:][a-zA-Z0-9_:]*$/;
/** valid label name (`__` prefixed names are reserved for prometheus) */
const LABEL_RE = /^[a-zA-Z_][a-zA-Z0-9_]*$/;

export type LabelValue = string | number | boolean;
export type Labels = Record<string, LabelValue>;

export type MetricType = 'counter' | 'gauge' | 'histogram';

/** a single labelled measurement within a counter/gauge family */
export type ScalarSample = { labels: Labels; value: number };

/** a single labelled measurement within a histogram family */
export type HistogramSample = {
  labels: Labels;
  /** per-bucket counts, parallel to the family's `buckets` (non-cumulative) */
  counts: number[];
  sum: number;
  count: number;
};

/** a serializable point-in-time view of a counter or gauge */
export type ScalarFamily = {
  type: 'counter' | 'gauge';
  name: string;
  help: string;
  samples: ScalarSample[];
};

/** a serializable point-in-time view of a histogram */
export type HistogramFamily = {
  type: 'histogram';
  name: string;
  help: string;
  buckets: number[];
  samples: HistogramSample[];
};

/** a serializable point-in-time view of one metric */
export type MetricFamily = ScalarFamily | HistogramFamily;

export type MetricOptions = {
  name: string;
  help?: string;
  /** label names this metric is allowed to use */
  labels?: string[];
};

export type HistogramOptions = MetricOptions & {
  /** upper bounds, ascending; `+Inf` is added automatically */
  buckets?: number[];
};

/** sane default spread for sub-second through multi-second observations */
export const DEFAULT_BUCKETS = [
  0.005, 0.01, 0.025, 0.05, 0.1, 0.25, 0.5, 1, 2.5, 5, 10,
];

export type RegistryLimits = {
  /** maximum number of registered metrics */
  maxMetrics: number;
  /** maximum number of label combinations per metric */
  maxSeries: number;
  /** maximum number of label names per metric */
  maxLabels: number;
};

export const NO_LIMITS: RegistryLimits = {
  maxMetrics: Infinity,
  maxSeries: Infinity,
  maxLabels: Infinity,
};

/** limits applied to plugin-registered metrics, which omegga does not control */
export const PLUGIN_LIMITS: RegistryLimits = {
  maxMetrics: 64,
  maxSeries: 1000,
  maxLabels: 8,
};

/**
 * Stable identity for a set of label values. Sorted so that `{a,b}` and
 * `{b,a}` land on the same series.
 */
function seriesKey(labels: Labels): string {
  const keys = Object.keys(labels).sort();
  if (keys.length === 0) return '';
  return keys.map(k => k + ' ' + String(labels[k])).join('');
}

/** normalize + validate user-supplied labels against the declared label names */
function checkLabels(
  metric: string,
  declared: string[],
  labels: Labels,
): Labels {
  const out: Labels = {};
  for (const [key, value] of Object.entries(labels)) {
    if (value == null) continue;
    if (!declared.includes(key)) {
      throw new Error(
        `metric "${metric}" was not declared with a "${key}" label`,
      );
    }
    out[key] = value;
  }
  return out;
}

function checkNumber(metric: string, value: number): number {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    throw new Error(`metric "${metric}" given a non-numeric value`);
  }
  return value;
}

abstract class Metric {
  abstract readonly type: MetricType;
  readonly name: string;
  readonly help: string;
  readonly labelNames: string[];
  protected limits: RegistryLimits;
  /** bumped by the owning registry when a write is dropped by the caps */
  onDrop: ((metric: string, reason: string) => void) | undefined;
  /** called when this metric's collect callback throws */
  onError: ((metric: string, err: unknown) => void) | undefined;

  constructor(opts: MetricOptions, limits: RegistryLimits) {
    if (!NAME_RE.test(opts.name)) {
      throw new Error(`invalid metric name "${opts.name}"`);
    }
    const labels = opts.labels ?? [];
    if (labels.length > limits.maxLabels) {
      throw new Error(
        `metric "${opts.name}" declares more than ${limits.maxLabels} labels`,
      );
    }
    for (const label of labels) {
      if (!LABEL_RE.test(label) || label.startsWith('__')) {
        throw new Error(`invalid label name "${label}" on "${opts.name}"`);
      }
    }

    this.name = opts.name;
    this.help = opts.help ?? opts.name;
    this.labelNames = labels;
    this.limits = limits;
  }

  abstract reset(): void;
  abstract snapshot(): MetricFamily;
  /** number of label combinations, without running any collect callback */
  abstract get size(): number;

  /** true when a new series may be created, false when the cap is hit */
  protected canGrow(size: number): boolean {
    if (size < this.limits.maxSeries) return true;
    this.onDrop?.(this.name, 'series limit reached');
    return false;
  }
}

/** shared series storage for the two scalar metric types */
abstract class ScalarMetric extends Metric {
  abstract readonly type: 'counter' | 'gauge';
  protected series = new Map<string, ScalarSample>();
  private collector:
    | (() => number | ScalarSample[] | void | undefined)
    | undefined;

  get size(): number {
    return this.series.size;
  }

  /** current value of one series, or 0 when it has never been written */
  get(labels: Labels = {}): number {
    return this.series.get(seriesKey(labels))?.value ?? 0;
  }

  /** drop a single series (e.g. a player that left, a plugin that unloaded) */
  remove(labels: Labels = {}): void {
    this.series.delete(seriesKey(labels));
  }

  reset(): void {
    this.series.clear();
  }

  /**
   * Register a callback that produces this metric's value at snapshot time.
   * Returning a number sets the unlabelled series; returning samples replaces
   * every series, which is how "one series per player/plugin" metrics stay in
   * sync with a list that shrinks (return `[]` to export nothing).
   *
   * On a counter this mirrors an externally-maintained cumulative value, such
   * as a process' CPU time. The source owns monotonicity, not this class.
   */
  collect(fn: () => number | ScalarSample[] | void | undefined): this {
    this.collector = fn;
    return this;
  }

  /** write a series directly, bypassing the monotonic guard on counters */
  protected write(labels: Labels, value: number): void {
    const key = seriesKey(labels);
    const existing = this.series.get(key);
    if (existing) {
      existing.value = value;
      return;
    }
    if (!this.canGrow(this.series.size)) return;
    this.series.set(key, { labels, value });
  }

  snapshot(): ScalarFamily {
    if (this.collector) {
      // a collect callback is often plugin code. letting it throw here would
      // fail the whole scrape, so a broken one keeps its last known series
      let result: number | ScalarSample[] | void | undefined;
      try {
        result = this.collector();
      } catch (err) {
        this.onError?.(this.name, err);
      }
      if (typeof result === 'number') {
        this.write({}, result);
      } else if (Array.isArray(result)) {
        this.series.clear();
        for (const sample of result) {
          if (!this.canGrow(this.series.size)) break;
          this.write(
            checkLabels(this.name, this.labelNames, sample.labels),
            sample.value,
          );
        }
      }
    }
    return {
      type: this.type,
      name: this.name,
      help: this.help,
      samples: [...this.series.values()].map(s => ({ ...s })),
    };
  }
}

/** a monotonically increasing count */
export class Counter extends ScalarMetric {
  readonly type = 'counter' as const;

  /** `inc(1)`, `inc(5)`, `inc({label}, 5)`, or `inc({label})` */
  inc(labelsOrValue: Labels | number = 1, maybeValue?: number): void {
    const hasLabels = typeof labelsOrValue === 'object';
    const labels = hasLabels
      ? checkLabels(this.name, this.labelNames, labelsOrValue)
      : {};
    const value = hasLabels ? (maybeValue ?? 1) : labelsOrValue;
    checkNumber(this.name, value);
    if (value < 0) {
      throw new Error(`counter "${this.name}" cannot decrease`);
    }

    this.write(labels, this.get(labels) + value);
  }
}

/** a value that can go up and down */
export class Gauge extends ScalarMetric {
  readonly type = 'gauge' as const;

  set(labelsOrValue: Labels | number, maybeValue?: number): void {
    const hasLabels = typeof labelsOrValue === 'object';
    const labels = hasLabels
      ? checkLabels(this.name, this.labelNames, labelsOrValue)
      : {};
    const value = hasLabels ? (maybeValue as number) : labelsOrValue;
    checkNumber(this.name, value);
    this.write(labels, value);
  }

  inc(labelsOrValue: Labels | number = 1, maybeValue?: number): void {
    const hasLabels = typeof labelsOrValue === 'object';
    const labels = hasLabels ? labelsOrValue : {};
    const delta = hasLabels ? (maybeValue ?? 1) : labelsOrValue;
    checkNumber(this.name, delta);
    this.set(labels, this.get(labels) + delta);
  }

  dec(labelsOrValue: Labels | number = 1, maybeValue?: number): void {
    const hasLabels = typeof labelsOrValue === 'object';
    const delta = hasLabels ? (maybeValue ?? 1) : labelsOrValue;
    checkNumber(this.name, delta);
    this.inc(hasLabels ? labelsOrValue : {}, -delta);
  }
}

/** bucketed observations, exported as `_bucket`/`_sum`/`_count` */
export class Histogram extends Metric {
  readonly type = 'histogram' as const;
  readonly buckets: number[];
  private series = new Map<string, HistogramSample>();

  get size(): number {
    return this.series.size;
  }

  constructor(opts: HistogramOptions, limits: RegistryLimits) {
    super(opts, limits);
    const buckets = (opts.buckets ?? DEFAULT_BUCKETS)
      .filter(b => typeof b === 'number' && !Number.isNaN(b) && b !== Infinity)
      .sort((a, b) => a - b);
    if (buckets.length === 0) {
      throw new Error(`histogram "${opts.name}" has no buckets`);
    }
    this.buckets = buckets;
  }

  observe(labelsOrValue: Labels | number, maybeValue?: number): void {
    const hasLabels = typeof labelsOrValue === 'object';
    const labels = hasLabels
      ? checkLabels(this.name, this.labelNames, labelsOrValue)
      : {};
    const value = hasLabels ? (maybeValue as number) : labelsOrValue;
    checkNumber(this.name, value);

    const key = seriesKey(labels);
    let sample = this.series.get(key);
    if (!sample) {
      if (!this.canGrow(this.series.size)) return;
      sample = {
        labels,
        counts: new Array(this.buckets.length + 1).fill(0),
        sum: 0,
        count: 0,
      };
      this.series.set(key, sample);
    }

    // the last slot is the implicit `+Inf` bucket
    let index = this.buckets.findIndex(b => value <= b);
    if (index === -1) index = this.buckets.length;
    sample.counts[index]++;
    sample.sum += value;
    sample.count++;
  }

  /** start a timer; the returned function observes the elapsed seconds */
  startTimer(labels: Labels = {}): () => number {
    const start = process.hrtime.bigint();
    return () => {
      const seconds = Number(process.hrtime.bigint() - start) / 1e9;
      this.observe(labels, seconds);
      return seconds;
    };
  }

  reset(): void {
    this.series.clear();
  }

  snapshot(): HistogramFamily {
    return {
      type: this.type,
      name: this.name,
      help: this.help,
      buckets: this.buckets,
      samples: [...this.series.values()].map(s => ({
        ...s,
        counts: [...s.counts],
      })),
    };
  }
}

/**
 * A named collection of metrics. `snapshot()` produces a serializable view;
 * rendering lives in ./render.ts so that host and plugin snapshots go through
 * exactly the same formatter.
 */
export class Registry {
  private metrics = new Map<string, Metric>();
  private limits: RegistryLimits;
  /** called when a cap rejects a registration or a new series */
  onDrop: ((metric: string, reason: string) => void) | undefined;
  /** called when a metric fails to collect; that metric is skipped */
  onError: ((metric: string, err: unknown) => void) | undefined;

  constructor(limits: RegistryLimits = NO_LIMITS) {
    this.limits = limits;
  }

  /**
   * Register (or return the already-registered) metric. Re-registering the
   * same name is how a collector re-attaches after a reload; the existing
   * instance wins so previously handed-out handles keep working.
   */
  private register<T extends Metric>(
    name: string,
    type: MetricType,
    create: () => T,
  ): T {
    const existing = this.metrics.get(name);
    if (existing) {
      // read the declared type rather than snapshotting, because a snapshot
      // would fire the metric's collect callback just to register a name
      if (existing.type !== type) {
        throw new Error(
          `metric "${name}" is already registered as a different type`,
        );
      }
      return existing as T;
    }
    if (this.metrics.size >= this.limits.maxMetrics) {
      this.onDrop?.(name, 'metric limit reached');
      throw new Error(
        `cannot register "${name}": metric limit (${this.limits.maxMetrics}) reached`,
      );
    }
    const metric = create();
    metric.onDrop = (m, reason) => this.onDrop?.(m, reason);
    metric.onError = (m, err) => this.onError?.(m, err);
    this.metrics.set(name, metric);
    return metric;
  }

  counter(opts: MetricOptions): Counter {
    return this.register(
      opts.name,
      'counter',
      () => new Counter(opts, this.limits),
    );
  }

  gauge(opts: MetricOptions): Gauge {
    return this.register(
      opts.name,
      'gauge',
      () => new Gauge(opts, this.limits),
    );
  }

  histogram(opts: HistogramOptions): Histogram {
    return this.register(
      opts.name,
      'histogram',
      () => new Histogram(opts, this.limits),
    );
  }

  /** permanently remove a metric (used when a plugin unloads) */
  remove(name: string): void {
    this.metrics.delete(name);
  }

  /** number of registered metrics */
  get size(): number {
    return this.metrics.size;
  }

  /** total number of label combinations across every metric */
  get seriesCount(): number {
    let total = 0;
    for (const metric of this.metrics.values()) total += metric.size;
    return total;
  }

  clear(): void {
    this.metrics.clear();
  }

  /** point-in-time view of every metric, sorted by name for stable output */
  snapshot(): MetricFamily[] {
    const families: MetricFamily[] = [];
    for (const metric of this.metrics.values()) {
      // one metric that cannot render must not cost a scrape every other one
      try {
        families.push(metric.snapshot());
      } catch (err) {
        this.onError?.(metric.name, err);
      }
    }
    return families.sort((a, b) =>
      a.name < b.name ? -1 : a.name > b.name ? 1 : 0,
    );
  }
}
